import React from 'react';
import { CreditApplicationColumnType, CreditApplicationFields } from '@dill/dill-shared';
import { X } from 'react-feather';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import { DillButton } from '../../../general/Dill';
import { DillDropdown } from '../../../general/Dill/DillDropdown';

type FieldType = CreditApplicationFields['fromField'];
type OptionsSource = 'CUSTOM' | 'SUPPLIER_BRANCHES' | 'SUPPLIER_SALES_REPS';

// Branch / sales rep options come from the field id, same as on the form (see
// conditionalOptionUtils.ts' resolveFieldOptions).
const SOURCE_ID_SUFFIX: Record<Exclude<OptionsSource, 'CUSTOM'>, string> = {
  SUPPLIER_BRANCHES: 'supplier-branches',
  SUPPLIER_SALES_REPS: 'supplier-sales-reps',
};

// One signer field being edited. Options are typed comma-separated; the id follows the label (and
// options source) until it's edited by hand.
export type SignerFieldDraft = {
  key: string;
  id: string;
  isIdEdited: boolean;
  name: string;
  fromField: FieldType;
  isRequired: boolean;
  optionsSource: OptionsSource;
  optionsText: string;
};

const TYPE_OPTIONS: { value: FieldType; label: string }[] = [
  { value: 'TEXT', label: 'Text' },
  { value: 'NUMBER', label: 'Number' },
  { value: 'CURRENCY', label: 'Currency' },
  { value: 'DATE', label: 'Date' },
  { value: 'TEXT_AREA', label: 'Text Area' },
  { value: 'CHECK_BOX', label: 'Checkbox' },
  { value: 'OPTION_FIELD', label: 'Option Field (e.g. Y / N)' },
  { value: 'DROPDOWN', label: 'Dropdown' },
];

const OPTIONS_SOURCE_OPTIONS: { value: OptionsSource; label: string }[] = [
  { value: 'CUSTOM', label: 'Custom options' },
  { value: 'SUPPLIER_BRANCHES', label: 'Supplier branches' },
  { value: 'SUPPLIER_SALES_REPS', label: 'Supplier sales reps' },
];

const COLUMN_OPTIONS: { value: CreditApplicationColumnType; label: string }[] = [
  { value: 'ONE_COLUMN', label: 'One column' },
  { value: 'TWO_COLUMNS', label: 'Two columns' },
  { value: 'THREE_COLUMNS', label: 'Three columns' },
];

const hasOptions = (fromField: FieldType) =>
  fromField === 'OPTION_FIELD' || fromField === 'DROPDOWN';

// DROPDOWN options are "::"-delimited, OPTION_FIELD ones comma-delimited.
const optionDelimiter = (fromField: FieldType) => (fromField === 'DROPDOWN' ? '::' : ',');

const slugify = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const toFieldId = (name: string, optionsSource: OptionsSource) =>
  optionsSource === 'CUSTOM'
    ? slugify(name)
    : [slugify(name), SOURCE_ID_SUFFIX[optionsSource]].filter(Boolean).join('-');

const optionsSourceForId = (id: string): OptionsSource =>
  id.includes(SOURCE_ID_SUFFIX.SUPPLIER_BRANCHES)
    ? 'SUPPLIER_BRANCHES'
    : id.includes(SOURCE_ID_SUFFIX.SUPPLIER_SALES_REPS)
    ? 'SUPPLIER_SALES_REPS'
    : 'CUSTOM';

export const toSignerFieldDrafts = (fields: CreditApplicationFields[] = []): SignerFieldDraft[] =>
  fields.map((field, index) => {
    const optionsSource = optionsSourceForId(field.id);
    return {
      key: `${field.id}-${index}`,
      id: field.id,
      isIdEdited: field.id !== toFieldId(field.name, optionsSource),
      name: field.name,
      fromField: field.fromField,
      isRequired: !!field.isRequired,
      optionsSource,
      optionsText: (field.optionList ?? '')
        .split(optionDelimiter(field.fromField))
        .map((option) => option.trim())
        .filter(Boolean)
        .join(', '),
    };
  });

// The fields to save, or the first problem with them. Existing fields keep any settings this editor
// doesn't show.
export const fromSignerFieldDrafts = (
  drafts: SignerFieldDraft[],
  existing: CreditApplicationFields[] = []
): { fields: CreditApplicationFields[]; error: string } => {
  const fields = drafts.map((draft): CreditApplicationFields => {
    const previous = existing.find((field) => field.id === draft.id);
    const options = draft.optionsText
      .split(',')
      .map((option) => option.trim())
      .filter(Boolean);
    return {
      ...(previous ?? { prefix: '', suffix: '' }),
      id: draft.id.trim(),
      name: draft.name.trim(),
      fromField: draft.fromField,
      isRequired: draft.isRequired,
      optionList:
        hasOptions(draft.fromField) && draft.optionsSource === 'CUSTOM'
          ? options.join(optionDelimiter(draft.fromField))
          : '',
    };
  });
  const ids = fields.map((field) => field.id);
  const duplicateId = ids.find((id, index) => ids.indexOf(id) !== index);
  const error = fields.some((field) => !field.name)
    ? 'Every field needs a label'
    : fields.some((field) => !field.id)
    ? 'Every field needs an ID'
    : duplicateId
    ? `Field IDs must be unique: ${duplicateId}`
    : drafts.some(
        (draft) =>
          hasOptions(draft.fromField) &&
          draft.optionsSource === 'CUSTOM' &&
          !draft.optionsText.trim()
      )
    ? 'Option fields and dropdowns with custom options need at least one option'
    : '';
  return { fields, error };
};

// The fields a signer step fills in itself (InternalFormSignerStep.fields), e.g. a Job Info
// Sheet's Internal Information. Same field shape as the form's; shown as an extra page from this
// signer on.
const SignerFieldsEditor = ({
  drafts,
  columnType,
  error,
  onChange,
  onColumnTypeChange,
}: {
  drafts: SignerFieldDraft[];
  columnType: CreditApplicationColumnType;
  error: string;
  onChange: (drafts: SignerFieldDraft[]) => void;
  onColumnTypeChange: (columnType: CreditApplicationColumnType) => void;
}) => {
  const update = (key: string, changes: Partial<SignerFieldDraft>) =>
    onChange(
      drafts.map((draft) => {
        if (draft.key !== key) return draft;
        const next = { ...draft, ...changes };
        return next.isIdEdited ? next : { ...next, id: toFieldId(next.name, next.optionsSource) };
      })
    );

  return (
    <div className="border border-gray-100 p-3 rounded-lg flex flex-col gap-2">
      <p className="text-xs font-medium text-gray-500">Fields this signer fills in (optional)</p>
      <p className="text-xs text-gray-400">
        Not part of the form: shown as an extra page to this signer (who has to fill in the required
        ones before signing) and to the signers after them. A Branch or Sales Rep field also decides
        who the next signers are (Branch TM, Region RSM, House Rep conditions).
      </p>
      {drafts.length > 1 && (
        <DillDropdown
          label="Layout"
          value={columnType}
          isSearchable={false}
          items={COLUMN_OPTIONS}
          onSelectChange={(item) => onColumnTypeChange(item.value as CreditApplicationColumnType)}
        />
      )}
      {drafts.map((draft) => (
        <div key={draft.key} className="border border-gray-200 rounded p-2 flex flex-col gap-2">
          <div className="flex gap-2 items-start">
            <div className="flex-1">
              <AppInputField
                label="Label"
                isRequired
                placeholder="e.g. Estimated Job $ Amount"
                value={draft.name}
                onTextChange={(text: string) => update(draft.key, { name: text })}
              />
            </div>
            <div className="flex-1">
              <AppInputField
                label="Field ID"
                isRequired
                placeholder="e.g. estimated-job-amount"
                value={draft.id}
                onTextChange={(text: string) =>
                  update(draft.key, { id: text.trim(), isIdEdited: true })
                }
              />
            </div>
            <div className="mt-5">
              <DillButton
                variant="icon-default"
                height="18px"
                width="18px"
                icon={<X size={10} />}
                onClick={() => onChange(drafts.filter((item) => item.key !== draft.key))}
              />
            </div>
          </div>
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <DillDropdown
                label="Type"
                value={draft.fromField}
                isSearchable={false}
                items={TYPE_OPTIONS}
                onSelectChange={(item) => {
                  const fromField = item.value as FieldType;
                  update(draft.key, {
                    fromField,
                    ...(hasOptions(fromField) ? {} : { optionsSource: 'CUSTOM' as const }),
                  });
                }}
              />
            </div>
            {hasOptions(draft.fromField) && (
              <div className="flex-1">
                <DillDropdown
                  label="Options from"
                  value={draft.optionsSource}
                  isSearchable={false}
                  items={OPTIONS_SOURCE_OPTIONS}
                  onSelectChange={(item) =>
                    update(draft.key, { optionsSource: item.value as OptionsSource })
                  }
                />
              </div>
            )}
          </div>
          {hasOptions(draft.fromField) && draft.optionsSource === 'CUSTOM' && (
            <AppInputField
              label="Options (comma-separated)"
              placeholder="e.g. Yes, No"
              value={draft.optionsText}
              onTextChange={(text: string) => update(draft.key, { optionsText: text })}
            />
          )}
          {hasOptions(draft.fromField) && draft.optionsSource !== 'CUSTOM' && (
            <p className="text-xs text-gray-400">
              {`The options are the supplier's ${
                draft.optionsSource === 'SUPPLIER_BRANCHES' ? 'branches' : 'sales reps'
              } (the field ID has to contain ${SOURCE_ID_SUFFIX[draft.optionsSource]}).`}
            </p>
          )}
          <label className="flex items-center gap-1 text-xs">
            <input
              type="checkbox"
              checked={draft.isRequired}
              onChange={(e) => update(draft.key, { isRequired: e.target.checked })}
            />
            Required
          </label>
        </div>
      ))}
      {error && <p className="text-xs text-red-500">{error}</p>}
      <DillButton
        label="Add Field"
        variant="secondary"
        onClick={() =>
          onChange([
            ...drafts,
            {
              key: `field-${Date.now()}`,
              id: '',
              isIdEdited: false,
              name: '',
              fromField: 'TEXT',
              isRequired: true,
              optionsSource: 'CUSTOM',
              optionsText: '',
            },
          ])
        }
      />
    </div>
  );
};

export default SignerFieldsEditor;
